import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  inventoryCountService,
  InventoryCountSession,
  DashboardSessionSummary,
  Warehouse,
} from '../../../../core/modules/inventoryCount/services/api';
import * as XLSX from 'xlsx';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TheoreticalUploadDialog from './TheoreticalUploadDialog';

const STATUS_LABEL: Record<InventoryCountSession['status'], string> = {
  draft: 'Borrador',
  active: 'Activa',
  closed: 'Cerrada',
  cancelled: 'Cancelada',
};

const STATUS_COLOR: Record<InventoryCountSession['status'], 'default' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  active: 'success',
  closed: 'info',
  cancelled: 'warning',
};

const InventoryCountAdminPage = () => {
  const { id: companyIdParam } = useParams<{ id: string }>();
  const companyId = Number(companyIdParam);
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<InventoryCountSession[]>([]);
  const [dashboardBySessionId, setDashboardBySessionId] = useState<Record<number, DashboardSessionSummary>>({});
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newWarehouseId, setNewWarehouseId] = useState<number | ''>('');
  const [newVarianceThresholdPct, setNewVarianceThresholdPct] = useState('10');
  const [creating, setCreating] = useState(false);

  // id de la sesion sobre la que hay una accion (cargar/activar) en curso
  const [actingSessionId, setActingSessionId] = useState<number | null>(null);
  // id de la sesion para la que esta abierto el dialogo de carga manual por archivo
  const [uploadDialogSessionId, setUploadDialogSessionId] = useState<number | null>(null);
  // que teorico se esta cargando en ese dialogo: propio (operador) o del
  // cliente dueno de la carga (caso 3PL, ej. AGUNSA)
  const [uploadDialogSourceType, setUploadDialogSourceType] = useState<'operator' | 'client'>('operator');


  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sessionsData, warehousesData, dashboardData] = await Promise.all([
        inventoryCountService.listSessions(companyId),
        inventoryCountService.listWarehouses(companyId),
        inventoryCountService.getDashboard(companyId),
      ]);
      setSessions(sessionsData);
      setWarehouses(warehousesData);
      setDashboardBySessionId(
        Object.fromEntries(dashboardData.sessions.map((s) => [s.id, s]))
      );
    } catch (err) {
      setError('No se pudo cargar la información de toma de inventario.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId, loadData]);

  const handleCreateSession = async () => {
    if (!newName.trim() || !newWarehouseId) {
      setError('Ingresa un nombre y selecciona una bodega para crear la sesión.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const parsedThreshold = Number(newVarianceThresholdPct);
      await inventoryCountService.createSession(companyId, {
        name: newName.trim(),
        warehouseId: Number(newWarehouseId),
        varianceThresholdPct: Number.isNaN(parsedThreshold) ? undefined : parsedThreshold,
      });
      setNewName('');
      setNewWarehouseId('');
      setNewVarianceThresholdPct('10');
      await loadData();
    } catch (err) {
      setError('No se pudo crear la sesión de conteo.');
    } finally {
      setCreating(false);
    }
  };

  const handleLoadAuto = async (sessionId: number) => {
    setActingSessionId(sessionId);
    setError(null);
    try {
      const result = await inventoryCountService.loadTheoreticalAuto(companyId, sessionId);
      if (result.loadedCount === 0) {
        setError('No se encontró stock calculado para esa bodega. Prueba con la carga manual.');
      }
      await loadData();
    } catch (err) {
      setError('No se pudo cargar el teórico automático.');
    } finally {
      setActingSessionId(null);
    }
  };

  const handleActivate = async (sessionId: number) => {
    setActingSessionId(sessionId);
    setError(null);
    try {
      await inventoryCountService.activateSession(companyId, sessionId);
      await loadData();
    } catch (err) {
      setError('No se pudo activar la sesión.');
    } finally {
      setActingSessionId(null);
    }
  };

  const handleDownloadTheoreticalTemplate = () => {
    const headers = ['SKU', 'Producto', 'Cantidad teórica', 'Ubicación'];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Plantilla');
    XLSX.writeFile(workbook, 'plantilla_teorico_inventario.xlsx');
  };

  const handleClose = async (sessionId: number) => {
    if (!window.confirm('Al cerrar la sesión, los tomadores ya no podrán contar ni recontar SKUs. ¿Continuar?')) {
      return;
    }
    setActingSessionId(sessionId);
    setError(null);
    try {
      await inventoryCountService.closeSession(companyId, sessionId);
      await loadData();
    } catch (err) {
      setError('No se pudo cerrar la sesión.');
    } finally {
      setActingSessionId(null);
    }
  };

  const handleDelete = async (sessionId: number, sessionName: string) => {
    if (
      !window.confirm(
        `¿Eliminar la sesión "${sessionName}"? Se borrará también el teórico que se haya cargado. Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    setActingSessionId(sessionId);
    setError(null);
    try {
      await inventoryCountService.deleteSession(companyId, sessionId);
      await loadData();
    } catch (err) {
      setError('No se pudo eliminar la sesión.');
    } finally {
      setActingSessionId(null);
    }
  };

  return (
    <Box className="container" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Toma de Inventario Físico
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Crea una sesión de conteo por bodega, carga el teórico (automático desde el stock
        que ya tiene Inventaria, o manual) y actívala para que los tomadores de inventario
        puedan empezar a contar.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Nueva sesión de conteo
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Nombre de la sesión"
                placeholder="Ej: Conteo trimestral Casa Matriz"
                fullWidth
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel id="warehouse-select-label">Bodega</InputLabel>
                <Select
                  labelId="warehouse-select-label"
                  label="Bodega"
                  value={newWarehouseId}
                  onChange={(e) => setNewWarehouseId(e.target.value as number)}
                >
                  {warehouses.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <TextField
                label="Umbral de alerta (%)"
                type="number"
                fullWidth
                value={newVarianceThresholdPct}
                onChange={(e) => setNewVarianceThresholdPct(e.target.value)}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Diferencia vs. teórico que dispara la alerta"
              />
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <Button
                variant="contained"
                fullWidth
                disabled={creating}
                onClick={handleCreateSession}
              >
                {creating ? <CircularProgress size={20} /> : 'Crear sesión'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Bodega</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Origen del teórico</TableCell>
                <TableCell align="right">Umbral alerta</TableCell>
                <TableCell align="right">SKUs cargados</TableCell>
                <TableCell align="right">Avance</TableCell>
                <TableCell align="right">Variaciones</TableCell>
                <TableCell>Tomadores</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Todavía no hay sesiones de conteo para esta empresa.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sessions.map((session) => {
                const isActing = actingSessionId === session.id;
                const dashboardInfo = dashboardBySessionId[session.id];
                return (
                  <TableRow key={session.id}>
                    <TableCell>{session.name}</TableCell>
                    <TableCell>{session.warehouseName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABEL[session.status]}
                        color={STATUS_COLOR[session.status]}
                      />
                    </TableCell>
                    <TableCell>
                      {session.source === 'auto' ? 'Automático (Inventaria)' : 'Archivo manual'}
                    </TableCell>
                    <TableCell align="right">{Number(session.varianceThresholdPct)}%</TableCell>
                    <TableCell align="right">{session.theoreticalItemsCount}</TableCell>
                    <TableCell align="right">
                      {dashboardInfo ? (
                        <Box sx={{ minWidth: 110 }}>
                          <Typography variant="body2">
                            {dashboardInfo.counted}/{dashboardInfo.total} SKUs
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dashboardInfo.percent}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={dashboardInfo.percent}
                            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                          />
                        </Box>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {dashboardInfo && dashboardInfo.skusWithVariance > 0 ? (
                        <Chip size="small" color="warning" label={dashboardInfo.skusWithVariance} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {dashboardInfo && dashboardInfo.participants.length > 0 ? (
                        <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ maxWidth: 220 }}>
                          {dashboardInfo.participants.map((p) => {
                            const label = p.fullName || p.email || `Usuario ${p.userId}`;
                            const firstName = label.split(' ')[0];
                            const initials = label
                              .split(' ')
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((s) => s[0]?.toUpperCase())
                              .join('');
                            return (
                              <Tooltip
                                key={p.userId}
                                title={
                                  <>
                                    {label}
                                    <br />
                                    {p.entriesCount} conteo{p.entriesCount === 1 ? '' : 's'} ·{' '}
                                    {formatDistanceToNow(new Date(p.lastActivityAt), { addSuffix: true, locale: es })}
                                  </>
                                }
                              >
                                <Chip
                                  size="small"
                                  avatar={
                                    <Avatar
                                      sx={{
                                        bgcolor: p.isRecentlyActive ? 'success.main' : 'grey.400',
                                        width: 24,
                                        height: 24,
                                        fontSize: 12,
                                      }}
                                    >
                                      {initials}
                                    </Avatar>
                                  }
                                  label={firstName}
                                  variant={p.isRecentlyActive ? 'filled' : 'outlined'}
                                  color={p.isRecentlyActive ? 'success' : 'default'}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
                        {session.status === 'draft' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isActing}
                              onClick={() => handleLoadAuto(session.id)}
                            >
                              Cargar automático
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={handleDownloadTheoreticalTemplate}
                            >
                              Descargar plantilla
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isActing}
                              onClick={() => {
                                setUploadDialogSourceType('operator');
                                setUploadDialogSessionId(session.id);
                              }}
                            >
                              Cargar teórico operador
                              {session.theoreticalOperatorItemsCount > 0 ? ` (${session.theoreticalOperatorItemsCount})` : ''}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isActing}
                              onClick={() => {
                                setUploadDialogSourceType('client');
                                setUploadDialogSessionId(session.id);
                              }}
                            >
                              Cargar teórico cliente
                              {session.theoreticalClientItemsCount > 0 ? ` (${session.theoreticalClientItemsCount})` : ''}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={isActing || session.theoreticalItemsCount === 0}
                              onClick={() => handleActivate(session.id)}
                            >
                              Activar
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteOutlineIcon fontSize="small" />}
                              disabled={isActing}
                              onClick={() => handleDelete(session.id, session.name)}
                            >
                              Eliminar
                            </Button>
                          </>
                        )}
                        {session.status === 'active' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={isActing}
                            onClick={() => handleClose(session.id)}
                          >
                            Cerrar
                          </Button>
                        )}
                        {(session.status === 'active' || session.status === 'closed') && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/dashboard/inventory-count/${companyId}/sessions/${session.id}/reconciliation`)}
                          >
                            Ver conciliación
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {uploadDialogSessionId !== null && (
        <TheoreticalUploadDialog
          isOpen
          companyId={companyId}
          sessionId={uploadDialogSessionId}
          sourceType={uploadDialogSourceType}
          onClose={() => setUploadDialogSessionId(null)}
          onImported={async () => {
            setUploadDialogSessionId(null);
            await loadData();
          }}
        />
      )}
    </Box>
  );
};

export default InventoryCountAdminPage;
